// [US 6.9 phase B] Client push (Android, FCM via @capacitor/push-notifications).
// Permission CONTEXTUELLE : on ne demande l'autorisation qu'au moment où l'utilisateur active
// le canal « push » dans l'écran d'alertes (jamais au lancement). Le plugin natif est importé
// dynamiquement (le bundle web n'en dépend pas). Web push = différé (phase ultérieure) → no-op sur web.
import { Capacitor } from '@capacitor/core';
import { sentinelClient } from './sentinelClient';

const LAST_TOKEN_KEY = 'ssiPushToken'; // dernier token enregistré (pour le désenregistrement)

// Push réellement disponible : natif uniquement en V1. Le web s'appuie sur e-mail + pull dashboard.
export const isPushSupported = (): boolean => Capacitor.isNativePlatform();

// Route de deep link déduite du payload `data` du push. PURE (testable sans plugin).
export const resolveDeepLinkPath = (data: Record<string, unknown> | undefined): string => {
    if (data && data.type === 'sentinel_tonight' && data.projectId) {
        return `/sentinel/projects/${data.projectId}`;
    }
    return '/sentinel/tonight';
};

type PushPlugin = typeof import('@capacitor/push-notifications')['PushNotifications'];
const REGISTER_TIMEOUT_MS = 15000;

// Obtient le token FCM courant : il arrive de façon asynchrone via l'événement 'registration'
// après register(). Les listeners sont retirés ensuite (ils s'accumulaient à chaque appel) — et
// ciblés par handle, pour ne pas emporter celui du deep link.
async function obtainToken(PushNotifications: PushPlugin): Promise<string> {
    const handles: { remove: () => Promise<void> }[] = [];
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        return await new Promise<string>((resolve, reject) => {
            timer = setTimeout(() => reject(new Error('registration_timeout')), REGISTER_TIMEOUT_MS);
            Promise.all([
                PushNotifications.addListener('registration', (t) => resolve(t.value)),
                PushNotifications.addListener('registrationError', (e) => reject(new Error(String((e as { error?: unknown })?.error ?? 'registrationError')))),
            ]).then((hs) => { handles.push(...hs); return PushNotifications.register(); }).catch(reject);
        });
    } finally {
        clearTimeout(timer);
        handles.forEach((h) => { void h.remove(); });
    }
}

// Active le push : permission contextuelle → enregistrement du token → POST serveur.
// @returns granted=false avec error ('unsupported' | 'denied' | 'register_failed') si non abouti.
export async function enablePush(locale: string): Promise<{ granted: boolean; error?: string }> {
    if (!isPushSupported()) return { granted: false, error: 'unsupported' };
    const { PushNotifications } = await import('@capacitor/push-notifications');

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
        perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return { granted: false, error: 'denied' };

    let token: string;
    try {
        token = await obtainToken(PushNotifications);
    } catch {
        return { granted: false, error: 'register_failed' };
    }

    localStorage.setItem(LAST_TOKEN_KEY, token);
    const ok = await sentinelClient.registerPushDevice(token, 'android', locale);
    return ok ? { granted: true } : { granted: false, error: 'register_failed' };
}

// Ré-enregistre le token au démarrage de l'app (canal push déjà choisi). FCM peut faire tourner le
// token (mise à jour de l'app, restauration…) et le serveur purge l'ancien au premier envoi refusé :
// sans ce rappel, le compte n'a plus aucun device et l'alerte est perdue en silence (cf. 2026-10-12).
// Ne demande JAMAIS la permission (elle reste contextuelle, cf. enablePush) : no-op si non accordée.
// Upsert serveur idempotent (ON CONFLICT token) → sans risque à chaque lancement.
export async function syncPushToken(locale: string): Promise<boolean> {
    if (!isPushSupported()) return false;
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== 'granted') return false;
    try {
        const token = await obtainToken(PushNotifications);
        localStorage.setItem(LAST_TOKEN_KEY, token);
        return await sentinelClient.registerPushDevice(token, 'android', locale);
    } catch {
        return false;
    }
}

// Désactive le push : désenregistre le token serveur. Pas de removeAllListeners() : obtainToken
// retire déjà ses propres listeners, et un retrait global emportait celui du deep link (initPushDeepLink).
export async function disablePush(): Promise<void> {
    const token = localStorage.getItem(LAST_TOKEN_KEY);
    if (token) {
        await sentinelClient.unregisterPushDevice(token);
        localStorage.removeItem(LAST_TOKEN_KEY);
    }
}

// Branche la navigation sur tap d'une notification (deep link). À appeler une fois à l'init (natif).
export async function initPushDeepLink(navigate: (path: string) => void): Promise<void> {
    if (!isPushSupported()) return;
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const data = (action?.notification?.data ?? undefined) as Record<string, unknown> | undefined;
        navigate(resolveDeepLinkPath(data));
    });
}
